import React, { type MouseEventHandler } from 'react';
import classNames from 'classnames';
import { NavLink } from 'react-router';

import Spinner from '../Spinner/Spinner';
import createInjectableComponent from '../../modules/createInjectableComponent';

import styles from './Button.module.scss';

export const ButtonIdentifier = Symbol(`BUTTON`);

type Color = 'default' | 'primary' | 'delete';

type Variant = 'contained' | 'outlined' | 'text' | 'danger' | 'delete';

export type ButtonProps = {
  children?: React.ReactNode;
  label: string;
  active?: boolean;
  color?: Color;
  fullWidth?: boolean;
  startIcon?: React.ReactElement;
  variant?: Variant;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  tabIndex?: number;
  size?: 'small' | 'medium' | 'large';
  to?: string;
  role?: string;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  busy?: boolean;
  id?: string;
  activeClassname?: string;
  target?: React.HTMLAttributeAnchorTarget;
} & React.AriaAttributes;

const Button: React.FC<ButtonProps> = ({
  label,
  children,
  color = 'default',
  startIcon,
  fullWidth = false,
  active = false,
  variant = 'outlined',
  size = 'medium',
  disabled,
  busy,
  type = 'button',
  to,
  onClick,
  className,
  activeClassname = '',
  target,
  ...rest
}: ButtonProps) => {
  const buttonClassName = (isActive: boolean) =>
    classNames(styles.button, className, styles[color], styles[variant], {
      [styles.active]: isActive,
      [activeClassname]: isActive,
      [styles.fullWidth]: fullWidth,
      [styles.large]: size === 'large',
      [styles.small]: size === 'small',
      [styles.disabled]: disabled,
    });

  const content = (
    <>
      {startIcon && <div className={styles.startIcon}>{startIcon}</div>}
      {<span className={classNames({ [styles.hidden]: busy }) || undefined}>{label}</span>}
      {children}
      {busy && <Spinner className={styles.centerAbsolute} size={'small'} />}
    </>
  );

  if (to) {
    return (
      <NavLink className={({ isActive }) => buttonClassName(isActive)} to={to} target={target} {...rest} end>
        {content}
      </NavLink>
    );
  }

  return (
    <button className={buttonClassName(active)} onClick={disabled ? undefined : onClick} type={type} aria-disabled={disabled} {...rest}>
      {content}
    </button>
  );
};
export default createInjectableComponent(ButtonIdentifier, Button);
